import { AvatarStack, IconButton, Tooltip } from '@tomo/ui';
import { ChevronRight, CloudOff, HardDrive, Menu as MenuIcon, PanelLeftOpen, Settings, Shapes, Trash2, Vote } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useStore } from '../../lib/store.ts';
import { useBoard, useCollection, useMeta, usePresence, useRecord, useSession, useSyncState } from '../../data/hooks.tsx';
import { Icon } from '../../lib/icons.tsx';
import { TopbarSlotTarget } from './TopbarSlot.tsx';
import { mobileNavOpen, sidebarCollapsed, toggleSidebar } from './ui-state.ts';

interface Crumb {
  label: string;
  icon?: ReactNode;
  to?: string;
}

function useCrumbs(): Crumb[] {
  const session = useSession();
  const meta = useMeta();
  const params = useParams();
  const { pathname } = useLocation();
  const base = `/p/${session.id}`;
  const record = useRecord(params.recordId);
  const collection = useCollection(params.collectionId ?? record?.collectionId);
  const board = useBoard(params.boardId);
  const crumbs: Crumb[] = [{ label: meta.name, icon: <Icon token={meta.icon} size={15} />, to: base }];
  if (collection) crumbs.push({ label: collection.name, icon: <span data-color={collection.color} style={{ color: 'var(--t-solid)', display: 'inline-flex' }}><Icon token={collection.icon} size={15} /></span>, to: `${base}/c/${collection.id}` });
  if (record) crumbs.push({ label: record.title || 'Untitled', icon: record.icon ? <Icon token={record.icon} size={15} /> : undefined });
  if (pathname.includes('/boards')) {
    crumbs.push({ label: 'Boards', icon: <Shapes />, to: `${base}/boards` });
    if (board) crumbs.push({ label: board.name });
  }
  if (pathname.endsWith('/votes')) crumbs.push({ label: 'Votes', icon: <Vote /> });
  if (pathname.endsWith('/trash')) crumbs.push({ label: 'Trash', icon: <Trash2 /> });
  if (pathname.endsWith('/settings')) crumbs.push({ label: 'Settings', icon: <Settings /> });
  return crumbs;
}

export function Topbar() {
  const crumbs = useCrumbs();
  const collapsed = useStore(sidebarCollapsed);
  return (
    <header className="topbar">
      <IconButton label="Open navigation" size="sm" className="topbar__menu" onClick={() => mobileNavOpen.set(true)}>
        <MenuIcon />
      </IconButton>
      {collapsed && (
        <IconButton label="Show sidebar" size="sm" tooltipShortcut="mod+\" className="topbar__collapse" onClick={toggleSidebar}>
          <PanelLeftOpen />
        </IconButton>
      )}
      <nav className="crumbs" aria-label="Breadcrumb">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          const inner = (
            <>
              {c.icon}
              <span className="crumb__label">{c.label}</span>
            </>
          );
          return (
            <Fragment key={i}>
              {i > 0 && <ChevronRight size={14} className="crumb-sep" />}
              {c.to && !last ? (
                <Link to={c.to} className="crumb">
                  {inner}
                </Link>
              ) : (
                <span className="crumb" aria-current={last ? 'page' : undefined}>
                  {inner}
                </span>
              )}
            </Fragment>
          );
        })}
      </nav>
      <TopbarSlotTarget className="topbar__actions" />
      <Presence />
      <SyncPill />
    </header>
  );
}

function Presence() {
  const people = usePresence();
  const others = people.filter((p) => !p.self);
  // One avatar per person even if they have several tabs open.
  const unique = [...new Map(others.map((p) => [p.id, p])).values()];
  if (!unique.length) return null;
  return <AvatarStack people={unique} size={24} ring max={4} />;
}

function SyncPill() {
  const sync = useSyncState();
  const session = useSession();
  const navigate = useNavigate();
  const open = () => navigate(`/p/${session.id}/settings#cloud`);
  if (sync.mode === 'local') {
    return (
      <Tooltip content="Saved on this device and works offline. Move it to a Pro workspace to sync and collaborate.">
        <button type="button" className="sync-pill" onClick={open}>
          <HardDrive />
          <span className="sync-pill__label">Local</span>
        </button>
      </Tooltip>
    );
  }
  const text: Record<string, string> = {
    synced: 'Synced',
    syncing: 'Syncing',
    connecting: 'Connecting',
    offline: 'Offline',
    'signed-out': 'Signed out',
    'upgrade-required': 'Sync paused',
    forbidden: 'No access',
    error: 'Sync error',
  };
  const hint =
    sync.status === 'offline'
      ? 'You are offline. Changes are saved on this device and sync when you reconnect.'
      : sync.message ?? (sync.readOnly ? 'You can view this project but not edit it.' : 'Changes sync in real time.');
  return (
    <Tooltip content={hint}>
      <button type="button" className="sync-pill" onClick={open}>
        {sync.status === 'offline' ? <CloudOff /> : <span className="sync-dot" data-state={sync.status} />}
        <span className="sync-pill__label">
          {text[sync.status] ?? sync.status}
          {sync.readOnly ? ' · view only' : ''}
        </span>
      </button>
    </Tooltip>
  );
}
